import React from 'react'
import { useTranslation } from 'react-i18next'
import { FormikErrors } from 'formik'
import set from 'lodash/fp/set'

import type { OnSubmit } from './types'

export function useSubmitErrorHandling<Values, Result extends Values>(submitHandler: OnSubmit<Values, Result>): OnSubmit<Values, Result> {
  const { t } = useTranslation()

  return React.useCallback(
    async (values, formikHelpers) => {
      try {
        return await submitHandler(values, formikHelpers)
      } catch (error) {
        if (isFormError(error)) {
          const errors: FormikErrors<Values> = error.unformattedErrors.reduce((errors, error) => {
            return set(error.FieldName, t(`Form.ValidationErrors.${error.ErrorType}`), errors)
          }, {})

          formikHelpers.setErrors(errors)
        }

        throw error
      }
    },
    [submitHandler, t]
  )
}

interface UnformattedError {
  ErrorType: string
  FieldName: string
}

interface FormError {
  unformattedErrors: UnformattedError[]
}

function isFormError(error: unknown): error is FormError {
  return typeof error === 'object' && error !== null && 'unformattedErrors' in error
}
